import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateSectionPromotion,
  useMenuSections,
  useSectionPromotions,
  useUpdateSectionPromotion,
} from "@/hooks/use-menu";
import { useActiveOrganization } from "@/hooks/use-organizations";
import type { UpsertSectionPromotionInput } from "@/types/menu";

const createEmptyFormState = (): UpsertSectionPromotionInput => ({
  name: "",
  slug: "",
  description: "",
  discount: 0,
  startDate: null,
  endDate: null,
  minQuantity: null,
  active: true,
  cron: null,
  sectionIds: [],
});

export default function SectionPromotionFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: activeOrganization, isLoading: isLoadingOrganization } = useActiveOrganization();
  const { data: promotionsData } = useSectionPromotions(activeOrganization?.id);
  const { data: sectionsData } = useMenuSections(activeOrganization?.id);
  const createPromotion = useCreateSectionPromotion();
  const updatePromotion = useUpdateSectionPromotion();
  const [formState, setFormState] = useState<UpsertSectionPromotionInput>(createEmptyFormState());

  const editingPromotion = id ? promotionsData?.promotions.find((p) => p.id === id) : undefined;

  useEffect(() => {
    if (editingPromotion) {
      setFormState({
        name: editingPromotion.name,
        slug: editingPromotion.slug,
        description: editingPromotion.description ?? "",
        discount: editingPromotion.discount,
        startDate: editingPromotion.startDate,
        endDate: editingPromotion.endDate,
        minQuantity: editingPromotion.minQuantity,
        active: editingPromotion.active,
        cron: editingPromotion.cron,
        sectionIds: editingPromotion.sectionIds ?? [],
      });
    }
  }, [editingPromotion]);

  const handleChange = <T extends keyof UpsertSectionPromotionInput>(
    field: T,
    value: UpsertSectionPromotionInput[T],
  ) => {
    setFormState((currentState) => ({
      ...currentState,
      [field]: value,
    }));
  };

  const handleSectionToggle = (sectionId: string) => {
    setFormState((currentState) => {
      const currentIds = currentState.sectionIds ?? [];
      const nextIds = currentIds.includes(sectionId)
        ? currentIds.filter((sid) => sid !== sectionId)
        : [...currentIds, sectionId];
      return { ...currentState, sectionIds: nextIds };
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const payload: UpsertSectionPromotionInput = {
      name: formState.name?.trim() ?? "",
      slug: formState.slug?.trim() || undefined,
      description: formState.description?.trim() || null,
      discount: formState.discount ?? 0,
      startDate: formState.startDate || null,
      endDate: formState.endDate || null,
      minQuantity: formState.minQuantity ?? null,
      active: formState.active ?? true,
      cron: formState.cron?.trim() || null,
      sectionIds: formState.sectionIds ?? [],
    };

    if (!payload.name) {
      toast.error(t("menu.promotions.shared.notifications.nameRequired"));
      return;
    }

    if (id) {
      updatePromotion.mutate(
        { data: payload, id },
        {
          onError: (mutationError) => {
            toast.error(mutationError.message);
          },
          onSuccess: () => {
            toast.success(t("menu.promotions.shared.notifications.updated"));
            navigate("/menu/promotions/section");
          },
        },
      );
      return;
    }

    createPromotion.mutate(payload, {
      onError: (mutationError) => {
        toast.error(mutationError.message);
      },
      onSuccess: () => {
        toast.success(t("menu.promotions.shared.notifications.created"));
        navigate("/menu/promotions/section");
      },
    });
  };

  if (isLoadingOrganization) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!activeOrganization) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("menu.promotions.section.title")}</CardTitle>
            <CardDescription>
              {t("menu.promotions.shared.notifications.activeOrgRequired")}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const formTitle = id
    ? t("menu.promotions.section.form.editTitle")
    : t("menu.promotions.section.form.createTitle");

  const submitLabel = id
    ? t("menu.promotions.shared.form.save")
    : t("menu.promotions.shared.form.create");

  return (
    <div className="flex flex-col gap-8 p-6">
      <div className="space-y-1">
        <h1 className="font-serif font-semibold text-2xl tracking-tight">{formTitle}</h1>
        <p className="text-muted-foreground text-sm">
          {t("menu.promotions.section.form.description")}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-serif">{formTitle}</CardTitle>
          <CardDescription>{t("menu.promotions.section.form.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-4" onSubmit={handleSubmit}>
            <div className="grid gap-2">
              <Label htmlFor="section-promo-name">{t("menu.promotions.shared.form.name")}</Label>
              <Input
                id="section-promo-name"
                onChange={(event) => handleChange("name", event.target.value)}
                placeholder={t("menu.promotions.shared.form.namePlaceholder")}
                value={formState.name ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="section-promo-slug">{t("menu.promotions.shared.form.slug")}</Label>
              <Input
                id="section-promo-slug"
                onChange={(event) => handleChange("slug", event.target.value)}
                placeholder={t("menu.promotions.shared.form.slugPlaceholder")}
                value={formState.slug ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="section-promo-description">
                {t("menu.promotions.shared.form.longDescription")}
              </Label>
              <Textarea
                id="section-promo-description"
                onChange={(event) => handleChange("description", event.target.value)}
                placeholder={t("menu.promotions.shared.form.descriptionPlaceholder")}
                rows={4}
                value={formState.description ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="section-promo-discount">
                {t("menu.promotions.shared.form.discount")}
              </Label>
              <Input
                id="section-promo-discount"
                max={100}
                min={0}
                onChange={(event) => handleChange("discount", Number(event.target.value))}
                type="number"
                value={formState.discount ?? 0}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="section-promo-start-date">
                {t("menu.promotions.shared.form.startDate")}
              </Label>
              <Input
                id="section-promo-start-date"
                onChange={(event) =>
                  handleChange(
                    "startDate",
                    event.target.value ? new Date(event.target.value).toISOString() : null,
                  )
                }
                type="datetime-local"
                value={
                  formState.startDate
                    ? new Date(formState.startDate).toISOString().slice(0, 16)
                    : ""
                }
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="section-promo-end-date">
                {t("menu.promotions.shared.form.endDate")}
              </Label>
              <Input
                id="section-promo-end-date"
                onChange={(event) =>
                  handleChange(
                    "endDate",
                    event.target.value ? new Date(event.target.value).toISOString() : null,
                  )
                }
                type="datetime-local"
                value={
                  formState.endDate ? new Date(formState.endDate).toISOString().slice(0, 16) : ""
                }
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="section-promo-min-quantity">
                {t("menu.promotions.shared.form.minQuantity")}
              </Label>
              <Input
                id="section-promo-min-quantity"
                onChange={(event) =>
                  handleChange(
                    "minQuantity",
                    event.target.value ? Number(event.target.value) : null,
                  )
                }
                type="number"
                value={formState.minQuantity ?? ""}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="section-promo-cron">{t("menu.promotions.shared.form.cron")}</Label>
              <Input
                id="section-promo-cron"
                onChange={(event) => handleChange("cron", event.target.value || null)}
                placeholder={t("menu.promotions.shared.form.cronPlaceholder")}
                value={formState.cron ?? ""}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-1">
                <p className="font-medium text-sm">{t("menu.promotions.shared.form.active")}</p>
                <p className="text-muted-foreground text-sm">
                  {t("menu.promotions.shared.form.activeDescription")}
                </p>
              </div>
              <Switch
                checked={formState.active ?? true}
                onCheckedChange={(checked) => handleChange("active", checked)}
              />
            </div>

            <div className="grid gap-2">
              <Label>{t("menu.promotions.section.form.selectSections")}</Label>
              <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border p-3">
                {sectionsData?.sections.length ? (
                  sectionsData.sections.map((section) => (
                    <label className="flex items-center gap-2 text-sm" key={section.id}>
                      <input
                        checked={formState.sectionIds?.includes(section.id) ?? false}
                        onChange={() => handleSectionToggle(section.id)}
                        type="checkbox"
                      />
                      {section.name}
                    </label>
                  ))
                ) : (
                  <p className="text-muted-foreground text-sm">
                    {t("menu.promotions.section.form.noSections")}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                disabled={createPromotion.isPending || updatePromotion.isPending}
                type="submit"
              >
                {submitLabel}
              </Button>
              <Button
                onClick={() => navigate("/menu/promotions/section")}
                type="button"
                variant="outline"
              >
                {t("menu.promotions.shared.form.cancel")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
